import { z } from 'zod';

// Human-readable fallback for every Zod validation message in Apply (Trello
// #2089). A schema's own message always wins; this map only replaces Zod's
// built-in English ("Invalid input: expected string, received undefined"),
// which customers saw on Continue when a required field was left empty.
//
// Wording is field-aware when the field is known (FIELD_COPY) and plain and
// generic otherwise — never a type name, a regex or a code.

type FieldCopy = {
  /** Noun phrase as it reads after "your" / "the": "first name". */
  noun: string;
  /** Text inputs are entered; dropdowns and pickers are selected. */
  action?: 'enter' | 'select';
  /** "your first name", "the financing amount", "a loan term". */
  determiner?: 'your' | 'the' | 'a';
};

// Keys as they appear in the forms. Owner fields repeat per owner
// (owner_1_first, owner_2_first…) and are normalised to "owner_first".
export const FIELD_COPY: Record<string, FieldCopy> = {
  loan_amount: { noun: 'financing amount', determiner: 'the' },
  loan_terms: { noun: 'loan term', action: 'select', determiner: 'a' },
  business_name: { noun: 'business name' },
  business_phone: { noun: 'business phone number' },
  business_street: { noun: 'business street address' },
  business_suite: { noun: 'suite or unit number' },
  business_city: { noun: 'business city' },
  business_state: { noun: 'business state', action: 'select' },
  business_zip: { noun: 'business ZIP code' },
  business_years: { noun: 'time in business', action: 'select' },
  business_revenue: { noun: 'annual revenue' },
  business_naics: { noun: 'industry', action: 'select' },
  business_structure: { noun: 'business structure', action: 'select' },
  business_ein: { noun: 'EIN' },
  business_website: { noun: 'website' },
  business_number_of_customers: { noun: 'number of customers', action: 'select' },
  home_based: { noun: 'answer', action: 'select' },
  equipment_description: { noun: 'equipment description', determiner: 'the' },
  when_funding_needed: { noun: 'timeline', action: 'select' },
  owner_first: { noun: 'first name' },
  owner_last: { noun: 'last name' },
  owner_email: { noun: 'email address' },
  owner_mobile: { noun: 'mobile phone number' },
  owner_phone: { noun: 'phone number' },
  owner_title: { noun: 'title' },
  owner_street: { noun: 'street address' },
  owner_suite: { noun: 'suite or unit number' },
  owner_city: { noun: 'city' },
  owner_state: { noun: 'state', action: 'select' },
  owner_zip: { noun: 'ZIP code' },
  owner_ssn: { noun: 'Social Security number' },
  owner_ownership: { noun: 'ownership percentage', determiner: 'the' },
  owner_dob: { noun: 'date of birth' },
  owner_dob_day: { noun: 'day of birth', action: 'select' },
  owner_dob_month: { noun: 'month of birth', action: 'select' },
  owner_dob_year: { noun: 'year of birth', action: 'select' },
  reasonWhy: { noun: 'reason', determiner: 'the' },
  extraQuestions: { noun: 'answer' },
  code: { noun: 'verification code', determiner: 'the' },
  cardName: { noun: 'name on the card', determiner: 'the' },
  cardNumber: { noun: 'card number', determiner: 'the' },
  cardExpiry: { noun: 'expiration date', determiner: 'the' },
  cardCvc: { noun: 'security code', determiner: 'the' },
  cardZip: { noun: 'billing ZIP code', determiner: 'the' },
};

/** "owner_2_first" → "owner_first"; other keys unchanged. */
export const normaliseFieldKey = (key: string) => key.replace(/^owner_\d+_/, 'owner_');

const fieldFromPath = (path: readonly PropertyKey[] | undefined): FieldCopy | undefined => {
  const key = [...(path ?? [])].reverse().find((segment) => typeof segment === 'string');
  return typeof key === 'string' ? FIELD_COPY[normaliseFieldKey(key)] : undefined;
};

const capitalise = (text: string) => (/^[A-Z]{2}/.test(text) ? text : text.charAt(0).toUpperCase() + text.slice(1));

const required = (field?: FieldCopy) => {
  if (!field) return 'This field is required.';
  const action = field.action ?? 'enter';
  return `Please ${action} ${field.determiner ?? 'your'} ${field.noun}.`;
};

const invalid = (field?: FieldCopy) => {
  if (!field) return 'Please check this field and try again.';
  return `Please ${field.action ?? 'enter'} a valid ${field.noun}.`;
};

const isEmpty = (input: unknown) =>
  input === undefined || input === null || (typeof input === 'string' && input.trim() === '') || Number.isNaN(input);

const asNumber = (value: unknown) => (typeof value === 'bigint' ? Number(value) : (value as number));

type Issue = z.core.$ZodRawIssue;

/** Message for a Zod issue that has no message of its own. Pure — unit-tested. */
export const humanZodMessage = (issue: Issue): string => {
  const field = fieldFromPath(issue.path);
  const Label = field ? capitalise(field.noun) : 'This value';

  switch (issue.code) {
    case 'invalid_type':
      return isEmpty(issue.input) ? required(field) : invalid(field);
    case 'too_small': {
      if (issue.origin === 'array' || issue.origin === 'set') return 'Please select at least one option.';
      const min = asNumber(issue.minimum);
      if (issue.origin === 'string') {
        if (min <= 1 || isEmpty(issue.input)) return required(field);
        return `${Label} must be at least ${min} characters.`;
      }
      if (min === 0 && !issue.inclusive) return `${Label} must be greater than 0.`;
      if (min === 0) return `${Label} can't be negative.`;
      return `${Label} must be at least ${min.toLocaleString('en-US')}.`;
    }
    case 'too_big': {
      const max = asNumber(issue.maximum);
      if (issue.origin === 'string') return `${Label} must be ${max} characters or fewer.`;
      if (issue.origin === 'array' || issue.origin === 'set') return `Please select no more than ${max}.`;
      return `${Label} can't be more than ${max.toLocaleString('en-US')}.`;
    }
    case 'invalid_format':
      if (issue.format === 'email') return 'Please enter a valid email address.';
      if (issue.format === 'url') return 'Please enter a valid website address.';
      return invalid(field);
    case 'invalid_value':
      return isEmpty(issue.input)
        ? required(field)
        : field
        ? `Please ${field.action ?? 'select'} a valid ${field.noun}.`
        : 'Please choose one of the options.';
    case 'not_multiple_of':
      return field ? `Please enter a valid ${field.noun}.` : 'Please enter a whole number.';
    default:
      return isEmpty(issue.input) ? required(field) : invalid(field);
  }
};

let installed = false;

/** Installs the map once; import for side effect at app start. */
export const installZodErrorMap = () => {
  if (installed) return;
  installed = true;
  z.config({ customError: (issue) => humanZodMessage(issue) });
};

installZodErrorMap();
